import AddToCartModalManager from 'global/addToCart/AddToCartModalManager';
import {
	shouldSkipModal,
	addToCartSkipModal,
} from 'global/addToCart/addToCartSkipModal';
import addToCartPanelTypeData from 'global/addToCart/addToCartPanelTypeData';
import { addToCart } from 'global/addToCart/addToCartModel';
import { getNodeFromString, generateSyncId } from 'global/common/globalUtils';
import { trackAddToCartTaggingData } from 'global/addToCart/addToCartTaggingData';

const { FormControl } = window.od;

class AddToCartFormControl extends FormControl {
	constructor(formNode) {
		super(formNode);

		this.canSubmit = true;

		this.qtyInput = this.form.querySelector(
			'.product-add-to-cart-panel__input',
		);

		this.minMaxElement = this.form.querySelector('.min-max-values');

		this.panelType = this.form.dataset.panelType;
		this.pubSubEventType = addToCartPanelTypeData(
			this.panelType,
			'pubSubEvent',
		);

		this.subscribeEvents();
	}

	subscribeEvents() {
		window.od.events.subscribe(
			`${this.pubSubEventType}_Submitting`,
			this.disableFormState.bind(this),
		);
		window.od.events.subscribe(
			`${this.pubSubEventType}_Submitted`,
			this.enableFormState.bind(this),
		);
		window.od.events.subscribe(
			`${this.pubSubEventType}_Error`,
			this.enableFormState.bind(this),
		);
		window.od.events.subscribe(
			'productPriceTable_RowSelected',
			this.updateQty.bind(this),
		);
	}

	onSubmit(e) {
		e.preventDefault();
		if (this.canSubmit && this.validateQty()) this.submitForm();
	}

	validateQty() {
		const { qty, qtyMin, qtyMax } = this.getFormData();
		if (!this.minMaxElement) return true;
		this.handleMinMaxClass('remove');
		return this.handleQtyValues(qty, qtyMin, qtyMax);
	}

	handleQtyValues(qty, qtyMin, qtyMax) {
		if (qty < qtyMin || qty > qtyMax) {
			this.handleMinMaxClass('add');
			return false;
		}
		return true;
	}

	handleMinMaxClass(action) {
		const minMaxClass = 'min-max-values--invalid-qty';
		if (action === 'add') {
			return this.minMaxElement.classList.add(minMaxClass);
		}
		if (action === 'remove') {
			this.minMaxElement.classList.remove(minMaxClass);
		}
		return null;
	}

	submitForm() {
		const formData = this.getFormData();

		window.od.events.publish(`${this.pubSubEventType}_Submitting`, {
			...formData,
			form: this.form,
		});

		return addToCart(formData)
			.then(({ data }) => getNodeFromString(data))
			.then(html => this.showResponseModal(html))
			.then(() => this.onSuccess())
			.catch(err => this.catchError(err));
	}

	showResponseModal(html) {
		if (shouldSkipModal(html)) {
			trackAddToCartTaggingData(html);
			return addToCartSkipModal(html);
		}

		return this.initModal(html);
	}

	getFormData() {
		const { qty, productCode } = this.form;
		const { qtyMin, qtyMax } = this.form.dataset;

		return {
			productCode: productCode.value,
			panelType: this.panelType,
			qty: parseInt(qty.value, 10),
			qtyMin: parseInt(qtyMin, 10),
			qtyMax: parseInt(qtyMax, 10),
		};
	}

	initModal(html) {
		const modalId = html.id || 'addToCart';
		const modal = new AddToCartModalManager();
		modal.insertResponse(html, modalId);
	}

	onSuccess() {
		this.resetQtyInput();

		window.od.events.publish(`${this.pubSubEventType}_Submitted`);
	}

	resetQtyInput() {
		const originalQty = this.form.qty.value;
		this.form.qty.value = this.form.qty.min || 1;

		if (this.form.qty.value !== originalQty) {
			window.od.events.publish(`${this.pubSubEventType}_QtyReset`, {
				articleNumber: this.form.productCode.value,
				qty: this.form.qty.value,
				sourceId: this.form.qty.dataset.syncId || generateSyncId(this.form.qty),
			});
		}
	}

	disableFormState() {
		this.canSubmit = false;
		this.disableFormFields();
	}

	enableFormState() {
		this.canSubmit = true;
		this.enableFormFields();
	}

	disableFormFields() {
		const { elements } = this.form;
		Array.from(elements).forEach(element => {
			element.setAttribute('readonly', '');
			element.setAttribute('disabled', '');
		});
	}

	enableFormFields() {
		const { elements } = this.form;
		Array.from(elements).forEach(element => {
			element.removeAttribute('readonly');
			element.removeAttribute('disabled');
		});
	}

	updateQty({ articleNumber, qty }) {
		if (this.form.productCode.value === articleNumber) {
			this.qtyInput.value = qty;
		}
	}

	catchError(err) {
		if (console) console.error(err); // eslint-disable-line no-console
		window.od.events.publish(`${this.pubSubEventType}_Error`);
	}
}

export default AddToCartFormControl;
